import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Tab,
    Tabs,
    TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid, { type WebGridColumn } from '../../../webgrid/src';
import { useAuth } from '../../contexts/AuthContext';
import {
    filterSearchColumns,
    filterSearchItems,
    formatSearchNumber,
    getSearchColumnWidths,
    getSearchDialogBounds,
    isValidSearchPeriod,
    numericSearchColumns,
    parseSearchWeight,
    searchCellText,
    searchItemColumns,
    type AdvancedSearchItem,
    type AdvancedSearchResults,
    type SearchItemKind,
    type SearchDialogBounds,
} from '../../domain/advancedContractSearch';
import { useApiRequest } from '../../hooks/useApiRequest';
import useRequest from '../../hooks/useRequest';
import { downloadBlob } from '../../utils/downloadBlob';
import ContractStatusChip from './ContractStatusChip';

interface Props {
    onClose: () => void;
    contentElement?: HTMLElement | null;
}

const renderSearchStatus = (_value: unknown, item: AdvancedSearchItem) => (
    <ContractStatusChip status={item.status} />
);

const AdvancedContractSearchDialog = ({ onClose, contentElement }: Props) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const request = useRequest();
    const { makeBlobRequest } = useApiRequest();
    const [startDate, setStartDate] = useState(() => dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(() => dayjs().format('YYYY-MM-DD'));
    const [kind, setKind] = useState<SearchItemKind>('metals');
    const [search, setSearch] = useState('');
    const [weight, setWeight] = useState('');
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
    const [results, setResults] = useState<AdvancedSearchResults | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [reload, setReload] = useState(0);
    const [contentBounds, setContentBounds] = useState<SearchDialogBounds | null>(null);
    const validPeriod = isValidSearchPeriod(startDate, endDate);
    const invalidWeight = kind === 'metals' && Number.isNaN(parseSearchWeight(weight));
    const role = user?.role?.toLowerCase();
    const canExport = role === 'admin' || role === 'administrator';

    useLayoutEffect(() => {
        if (!contentElement) return;
        const measureContent = () => {
            const sidebarRight =
                contentElement.ownerDocument
                    .querySelector('.MuiDrawer-paper')
                    ?.getBoundingClientRect().right ?? 0;
            setContentBounds(
                getSearchDialogBounds(
                    contentElement.getBoundingClientRect(),
                    window.innerWidth,
                    window.innerHeight,
                    sidebarRight,
                ),
            );
        };
        measureContent();
        const observer = new ResizeObserver(measureContent);
        observer.observe(contentElement);
        window.addEventListener('resize', measureContent);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measureContent);
        };
    }, [contentElement]);

    useEffect(() => {
        let cancelled = false;
        setResults(null);
        setError(null);
        setLoading(validPeriod);
        if (!validPeriod) return;
        request<AdvancedSearchResults>({
            path: '/contracts/advanced-search',
            params: { startDate, endDate },
        })
            .then(data => {
                if (!cancelled) setResults(data);
            })
            .catch(() => {
                if (!cancelled) setError(t('contracts.advancedSearch.loadFailed'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [startDate, endDate, validPeriod, request, reload, t]);

    const gridWidth = Math.max(1, (contentBounds?.width ?? window.innerWidth) - 56);
    const columns = useMemo<WebGridColumn<AdvancedSearchItem>[]>(() => {
        const widths = getSearchColumnWidths(kind, gridWidth);
        return searchItemColumns(kind).map(key => {
            let render: WebGridColumn<AdvancedSearchItem>['render'] = numericSearchColumns.has(key)
                ? formatSearchNumber
                : undefined;
            if (key === 'status') render = renderSearchStatus;
            return {
                key,
                dataIndex: key,
                title: t(`contracts.advancedSearch.columns.${key}`),
                sortable: true,
                filterable: true,
                width: widths[key],
                render,
                getFilterValue: item => searchCellText(item, key),
            };
        });
    }, [kind, gridWidth, t]);
    const rows = useMemo(
        () => filterSearchItems(results?.[kind] ?? [], kind, search, weight),
        [results, kind, search, weight],
    );
    const exportableRows = useMemo(
        () => filterSearchColumns(rows, kind, columnFilters),
        [rows, kind, columnFilters],
    );

    const exportRows = async (
        filteredRows: AdvancedSearchItem[],
        exportedColumns: WebGridColumn<AdvancedSearchItem>[],
    ) => {
        if (!canExport || filteredRows.length === 0 || exporting) return;
        setExporting(true);
        setError(null);
        try {
            const blob = await makeBlobRequest('/contracts/advanced-search/export', {
                method: 'POST',
                params: {
                    startDate,
                    endDate,
                    kind,
                    ids: filteredRows.map(item => item.id),
                    headers: exportedColumns.map(column => column.title),
                },
            });
            downloadBlob(blob, `advanced-search_${kind}_${startDate}_${endDate}.xlsx`);
        } catch {
            setError(t('contracts.advancedSearch.exportFailed'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth={false}
            fullWidth
            aria-labelledby="advanced-search-title"
            sx={{
                left: contentBounds?.left,
                top: contentBounds?.top,
                width: contentBounds?.width,
                height: contentBounds?.height,
                '& .MuiDialog-paper': {
                    width: 'calc(100% - 24px)',
                    maxWidth: 'none',
                    maxHeight: 'calc(100% - 24px)',
                    m: '12px',
                },
            }}
        >
            <DialogTitle id="advanced-search-title">
                {t('contracts.advancedSearch.title')}
            </DialogTitle>
            <DialogContent
                sx={{
                    px: '12px',
                    '& .MuiTable-root': { minWidth: 0, width: '100%' },
                    '& .MuiTableCell-root': { p: '4px', fontSize: '0.65rem' },
                    '& .MuiTableCell-head, & .MuiTableSortLabel-root': { whiteSpace: 'normal' },
                    '& .MuiTableSortLabel-icon': { width: 12, height: 12, mx: '1px' },
                    '& .MuiChip-root': { maxWidth: '100%', height: 20, fontSize: '0.6rem' },
                    '& .MuiChip-label': { px: '4px' },
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, mb: 2 }}>
                    <TextField
                        label={t('contracts.advancedSearch.startDate')}
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={event => setStartDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={!validPeriod}
                    />
                    <TextField
                        label={t('contracts.advancedSearch.endDate')}
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={event => setEndDate(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={!validPeriod}
                    />
                    <TextField
                        label={t('contracts.advancedSearch.search')}
                        size="small"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        sx={{ flex: 1 }}
                    />
                    {kind === 'metals' && (
                        <TextField
                            label={t('contracts.advancedSearch.weight')}
                            size="small"
                            value={weight}
                            onChange={event => setWeight(event.target.value)}
                            error={invalidWeight}
                            helperText={
                                invalidWeight ? t('contracts.advancedSearch.invalidWeight') : ''
                            }
                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                        />
                    )}
                </Stack>
                {!validPeriod && (
                    <Alert severity="warning">{t('contracts.advancedSearch.invalidPeriod')}</Alert>
                )}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 1 }}
                        action={
                            <Button color="inherit" onClick={() => setReload(value => value + 1)}>
                                {t('contracts.advancedSearch.retry')}
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}
                <Tabs
                    value={kind}
                    onChange={(_event, value: SearchItemKind) => {
                        setKind(value);
                        setSearch('');
                        setColumnFilters({});
                    }}
                    aria-label={t('contracts.advancedSearch.itemTypes')}
                    sx={{ mb: 1 }}
                >
                    <Tab value="metals" label={t('contracts.advancedSearch.metals')} />
                    <Tab value="electronics" label={t('contracts.advancedSearch.electronics')} />
                </Tabs>
                {canExport && (
                    <Button
                        variant="outlined"
                        sx={{ mb: 1 }}
                        disabled={
                            loading || !validPeriod || exporting || exportableRows.length === 0
                        }
                        onClick={() => {
                            void exportRows(exportableRows, columns);
                        }}
                    >
                        {t('contracts.advancedSearch.export')}
                    </Button>
                )}
                {exporting && (
                    <Alert severity="info">{t('contracts.advancedSearch.exporting')}</Alert>
                )}
                {loading && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress aria-label={t('contracts.advancedSearch.loading')} />
                    </Box>
                )}
                {!loading && validPeriod && results && (
                    <WebGrid<AdvancedSearchItem>
                        key={kind}
                        data={rows}
                        columns={columns}
                        filterBar
                        pagination
                        pageSize={20}
                        selectable={false}
                        exportable={false}
                        onFilterChange={setColumnFilters}
                        rowIdAccessor={item => item.id}
                        height="min(55vh, 500px)"
                        emptyMessage={t('contracts.advancedSearch.empty')}
                        storageKey={`contracts-advanced-search-${kind}-compact`}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('contracts.advancedSearch.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdvancedContractSearchDialog;
